export function skipExportNodes(node: HTMLElement): boolean {
  return !(node.dataset?.exportSkip === 'true')
}

const CANVAS_SIDE_CAP_PX = 16000
const CANVAS_AREA_CAP_PX = 2.6e8

/** Canvas-cap guard (spec O2): browsers cap rasterized canvas dimensions (both a max
 *  side length and a max total area). A flat `pixelRatio: 2` capture of a very tall
 *  page-level dashboard can exceed those caps and silently produce a blank/oversized
 *  canvas. Returns the largest ratio <= `desired` that keeps BOTH caps satisfied:
 *   - side cap:  width*ratio  <= 16000  AND  height*ratio <= 16000
 *   - area cap:  width*height*ratio^2  <= 2.6e8
 *  Never returns above `desired`. Zero/negative/non-finite dimensions have nothing
 *  to clamp against, so they fall back to `desired` rather than producing NaN/Infinity. */
export function safePixelRatio(width: number, height: number, desired = 2): number {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return desired
  }
  const sideCapRatio = Math.min(CANVAS_SIDE_CAP_PX / width, CANVAS_SIDE_CAP_PX / height)
  const areaCapRatio = Math.sqrt(CANVAS_AREA_CAP_PX / (width * height))
  return Math.min(desired, sideCapRatio, areaCapRatio)
}

/** Emits a dev-visible warning (no-silent-fallbacks doctrine) whenever the canvas-cap
 *  guard actually reduced the capture resolution below what was asked for — a clamp is
 *  a real degradation of export quality and must not pass unnoticed. */
export function warnIfClamped(
  context: string,
  width: number,
  height: number,
  ratio: number,
  desired = 2,
): void {
  if (ratio < desired) {
    console.warn(
      `${context}: canvas-cap guard reduced pixelRatio from ${desired} to ${ratio.toFixed(3)} for a ${width}x${height} element, to avoid a blank/oversized canvas`,
    )
  }
}

function triggerDownload(dataUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export async function exportAsImage(el: HTMLElement, filename: string): Promise<void> {
  const { toPng } = await import('html-to-image')
  const pixelRatio = safePixelRatio(el.scrollWidth, el.scrollHeight)
  warnIfClamped('exportAsImage', el.scrollWidth, el.scrollHeight, pixelRatio)
  const dataUrl = await toPng(el, {
    pixelRatio,
    cacheBust: true,
    filter: skipExportNodes,
  })
  triggerDownload(dataUrl, filename)
}
